import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Check, Gift, Users, Trophy, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../config';


const tierColors = {
  bronze: { bg: 'bg-amber-700/10', border: 'border-amber-700/30', text: 'text-amber-600', fill: 'bg-amber-700' },
  silver: { bg: 'bg-zinc-300/10', border: 'border-zinc-400/30', text: 'text-foreground/80', fill: 'bg-zinc-400' },
  gold: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', fill: 'bg-yellow-500' },
  diamond: { bg: 'bg-cyan-400/10', border: 'border-cyan-400/30', text: 'text-cyan-400', fill: 'bg-cyan-400' },
};

const labels = {
  en: {
    title: 'Referral Program',
    subtitle: 'Invite friends, earn rewards',
    your_link: 'Your referral link',
    copied: 'Copied!',
    copy: 'Copy',
    stats_total: 'Total referrals',
    stats_tier: 'Current tier',
    stats_next: 'Next tier',
    referrals_away: 'referral(s) to go',
    no_tier: 'No tier yet',
    rules_link: 'View referral program rules',
    rewards_title: 'Rewards',
    recent_title: 'Recent referrals',
    no_referrals: 'Share your link to start earning rewards!',
    tier_progress: 'Tier progress',
    leaderboard: 'Leaderboard',
    reward_labels: {
      early_access_1month: '+1 month early access',
      discount_5: '5% discount on your next license purchase',
      discount_10: '10% discount on your next license purchase',
      free_standard: 'Free Standard license (1 device)',
      free_founder: 'Free Founder Edition (1 device)',
    },
  },
  fr: {
    title: 'Programme de parrainage',
    subtitle: 'Invitez vos amis, gagnez des récompenses',
    your_link: 'Votre lien de parrainage',
    copied: 'Copié !',
    copy: 'Copier',
    stats_total: 'Parrainages totaux',
    stats_tier: 'Palier actuel',
    stats_next: 'Prochain palier',
    referrals_away: 'parrainage(s) restant(s)',
    no_tier: 'Aucun palier',
    rules_link: 'Voir les règles du programme de parrainage',
    rewards_title: 'Récompenses',
    recent_title: 'Parrainages récents',
    no_referrals: 'Partagez votre lien pour commencer à gagner des récompenses !',
    tier_progress: 'Progression',
    leaderboard: 'Classement',
    reward_labels: {
      early_access_1month: "+1 mois d'accès anticipé",
      discount_5: '5% de réduction sur votre prochain achat de licence',
      discount_10: '10% de réduction sur votre prochain achat de licence',
      free_standard: 'Licence Standard gratuite (1 appareil)',
      free_founder: 'Édition Fondateur gratuite (1 appareil)',
    },
  },
  hi: {
    title: 'रेफरल कार्यक्रम',
    subtitle: 'दोस्तों को आमंत्रित करें, पुरस्कार अर्जित करें',
    your_link: 'आपका रेफरल लिंक',
    copied: 'कॉपी हो गया!',
    copy: 'कॉपी',
    stats_total: 'कुल रेफरल',
    stats_tier: 'वर्तमान स्तर',
    stats_next: 'अगला स्तर',
    referrals_away: 'रेफरल शेष',
    no_tier: 'अभी कोई स्तर नहीं',
    rules_link: 'रेफरल कार्यक्रम के नियम देखें',
    rewards_title: 'पुरस्कार',
    recent_title: 'हालिया रेफरल',
    no_referrals: 'पुरस्कार अर्जित करने के लिए अपना लिंक साझा करें!',
    tier_progress: 'स्तर प्रगति',
    leaderboard: 'लीडरबोर्ड',
    reward_labels: {
      early_access_1month: '+1 महीना अर्ली एक्सेस',
      discount_5: 'अगली लाइसेंस खरीद पर 5% छूट',
      discount_10: 'अगली लाइसेंस खरीद पर 10% छूट',
      free_standard: 'मुफ़्त स्टैंडर्ड लाइसेंस (1 डिवाइस)',
      free_founder: 'मुफ़्त फाउंडर एडिशन (1 डिवाइस)',
    },
  },
};

export const ReferralDashboard = () => {
  const { lang } = useI18n();
  const { getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);
  const l = labels[lang] || labels.en;

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    axios.get(`${API}/referral/stats`, { headers }).then(r => {
      const data = r.data;
      // Override referral_link with correct frontend URL
      data.referral_link = `${window.location.origin}?ref=${data.referral_code}`;
      setStats(data);
    }).catch(() => {});
    axios.get(`${API}/referral/tiers`).then(r => setTiers(r.data.tiers || [])).catch(() => {});
    axios.get(`${API}/referral/leaderboard`).then(r => setLeaderboard(r.data.leaderboard || [])).catch(() => {});
  }, [getToken]);

  const handleCopy = () => {
    if (!stats?.referral_link) return;
    navigator.clipboard.writeText(stats.referral_link);
    setCopied(true);
    toast.success(l.copied);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!stats) return null;

  const currentTierColor = tierColors[stats.current_tier] || null;
  const nextTierColor = tierColors[stats.next_tier] || null;

  // Calculate progress bar
  const currentMin = stats.current_tier
    ? tiers.find(t => t.id === stats.current_tier)?.min_referrals || 0
    : 0;
  const nextMin = stats.next_tier
    ? tiers.find(t => t.id === stats.next_tier)?.min_referrals || 1
    : stats.total_referrals + 1;
  const progressPct = stats.next_tier
    ? Math.min(((stats.total_referrals - currentMin) / (nextMin - currentMin)) * 100, 100)
    : 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="space-y-6"
      data-testid="referral-dashboard"
    >
      {/* Header */}
      <div className="p-6 rounded-2xl bg-card border border-border">
        <div className="flex items-center gap-3 mb-1">
          <Gift className="w-5 h-5 text-blue-500" />
          <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium">{l.title}</h2>
        </div>
        <p className="text-muted-foreground text-sm mb-5">{l.subtitle}</p>

        {/* Referral link */}
        <div className="mb-6">
          <label className="text-xs text-muted-foreground mb-1.5 block">{l.your_link}</label>
          <div className="flex gap-2">
            <div className="flex-1 px-4 py-2.5 rounded-lg bg-secondary border border-border text-foreground/80 text-sm truncate" data-testid="referral-link">
              {stats.referral_link}
            </div>
            <Button
              onClick={handleCopy}
              className="shrink-0 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-4 transition-all"
              data-testid="referral-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          {/* Social share buttons */}
          {stats?.referral_link && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="referral-share-buttons">
              <a href={`https://wa.me/?text=${encodeURIComponent((lang === 'fr' ? 'Découvre HexoPDF, un éditeur PDF offline sans abonnement ! ' : 'Check out HexoPDF, an offline PDF editor with no subscription! ') + stats.referral_link)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366] text-xs hover:bg-[#25D366]/20 transition-colors"
                data-testid="share-whatsapp">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a href={`https://t.me/share/url?url=${encodeURIComponent(stats.referral_link)}&text=${encodeURIComponent(lang === 'fr' ? 'Découvre HexoPDF, un éditeur PDF offline sans abonnement !' : 'Check out HexoPDF, an offline PDF editor with no subscription!')}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0088cc]/10 border border-[#0088cc]/20 text-[#0088cc] text-xs hover:bg-[#0088cc]/20 transition-colors"
                data-testid="share-telegram">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                Telegram
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(stats.referral_link)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1877F2]/10 border border-[#1877F2]/20 text-[#1877F2] text-xs hover:bg-[#1877F2]/20 transition-colors"
                data-testid="share-facebook">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </a>
              <a href={`https://www.instagram.com/`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E4405F]/10 border border-[#E4405F]/20 text-[#E4405F] text-xs hover:bg-[#E4405F]/20 transition-colors"
                data-testid="share-instagram">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>
                Instagram
              </a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent((lang === 'fr' ? 'Découvre HexoPDF ! ' : 'Check out HexoPDF! ') + stats.referral_link)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground/5 border border-foreground/10 text-foreground/60 text-xs hover:bg-foreground/10 transition-colors"
                data-testid="share-x">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X
              </a>
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-xl bg-secondary border border-border text-center">
            <Users className="w-4 h-4 text-blue-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-foreground" data-testid="referral-total-count">{stats.total_referrals}</p>
            <p className="text-xs text-muted-foreground">{l.stats_total}</p>
          </div>
          <div className="p-3 rounded-xl bg-secondary border border-border text-center">
            <Trophy className={`w-4 h-4 mx-auto mb-1 ${currentTierColor ? currentTierColor.text : 'text-muted-foreground/60'}`} />
            <p className={`text-lg font-bold ${currentTierColor ? currentTierColor.text : 'text-muted-foreground/60'}`} data-testid="referral-current-tier">
              {stats.current_tier_label || l.no_tier}
            </p>
            <p className="text-xs text-muted-foreground">{l.stats_tier}</p>
          </div>
          <div className="p-3 rounded-xl bg-secondary border border-border text-center">
            <ChevronRight className={`w-4 h-4 mx-auto mb-1 ${nextTierColor ? nextTierColor.text : 'text-muted-foreground/60'}`} />
            <p className={`text-lg font-bold ${nextTierColor ? nextTierColor.text : 'text-muted-foreground/60'}`} data-testid="referral-next-tier">
              {stats.next_tier_label || ' · '}
            </p>
            <p className="text-xs text-muted-foreground">{l.stats_next}</p>
          </div>
        </div>

        {/* Progress bar */}
        {stats.next_tier && (
          <div className="mt-4" data-testid="referral-progress">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">{l.tier_progress}</span>
              <span className="text-muted-foreground/70">
                {stats.referrals_to_next_tier} {l.referrals_away}
              </span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                className={`h-full rounded-full ${nextTierColor ? nextTierColor.fill : 'bg-blue-500'}`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Tier cards */}
      <div className="p-6 rounded-2xl bg-card border border-border">
        <h3 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4">{l.rewards_title}</h3>
        <div className="grid grid-cols-2 gap-3">
          {tiers.map((tier) => {
            const tc = tierColors[tier.id] || tierColors.bronze;
            const isUnlocked = stats.total_referrals >= tier.min_referrals;
            const rewardLabel = l.reward_labels[tier.reward_key] || tier.reward_labels?.[lang] || tier.reward_labels?.en || tier.reward_key;
            return (
              <div
                key={tier.id}
                className={`p-3 rounded-xl border transition-all ${isUnlocked ? `${tc.bg} ${tc.border}` : 'bg-secondary/50 border-border opacity-60'}`}
                data-testid={`referral-tier-${tier.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className={`w-3.5 h-3.5 ${isUnlocked ? tc.text : 'text-muted-foreground/60'}`} />
                  <span className={`text-sm font-semibold ${isUnlocked ? tc.text : 'text-muted-foreground'}`}>{tier.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{tier.min_referrals}+ referrals</p>
                <p className={`text-xs mt-1 ${isUnlocked ? 'text-foreground/90' : 'text-muted-foreground'}`}>{rewardLabel}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="p-6 rounded-2xl bg-card border border-border">
          <h3 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4">{l.leaderboard}</h3>
          <div className="space-y-2">
            {leaderboard.map((entry) => {
              const tc = tierColors[entry.tier] || tierColors.bronze;
              return (
                <div key={entry.rank} className="flex items-center gap-3 p-2.5 rounded-lg bg-secondary/50" data-testid={`leaderboard-rank-${entry.rank}`}>
                  <span className="w-6 text-center text-sm font-bold text-muted-foreground">#{entry.rank}</span>
                  <span className="flex-1 text-sm text-foreground/80">{entry.email}</span>
                  <span className={`text-xs font-medium ${tc.text}`}>{entry.tier?.toUpperCase()}</span>
                  <span className="text-xs text-muted-foreground">{entry.referrals}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent referrals */}
      {stats.recent_referrals?.length > 0 && (
        <div className="p-6 rounded-2xl bg-card border border-border">
          <h3 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4">{l.recent_title}</h3>
          <div className="space-y-2">
            {stats.recent_referrals.map((ref, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/50">
                <span className="text-sm text-foreground/80">{ref.referred_email}</span>
                <span className="text-xs text-muted-foreground">{ref.event_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="text-center">
        <a href="/referral-rules" className="text-blue-400 hover:text-blue-300 text-xs underline transition-colors" data-testid="referral-rules-link">
          {l.rules_link || 'View referral program rules'}
        </a>
      </div>
    </motion.div>
  );
};
